<template>
  <DsPageContent
    class="ds-background-color-grey-025 ds-padding-bottom-48 ds-md-padding-bottom-96"
  >
    <DsHero theme="grey" :title="$labels.homepageTitle" />

    <section class="status-section">
      <div class="ds-container ds-padding-top-48">
        <div v-if="showUptimeBar" class="ds-grid ds-justify-content-center">
          <div class="ds-grid__col ds-grid__col--12 ds-lg-grid__col--10">
            <StatusUptime
              :start-date="uptimeDates.startDate"
              :end-date="uptimeDates.endDate"
              :logs="severeMessagesPerDay"
              class="ds-margin-bottom-24 ds-md-margin-bottom-48"
            />
          </div>
        </div>
        <div class="ds-grid ds-justify-content-center">
          <div class="ds-grid__col ds-grid__col--12 ds-lg-grid__col--10">
            <StatusOverview
              :status="overviewStatus"
              :items="overviewItems"
              :legend-items="legendItems"
            />
          </div>
        </div>
      </div>
    </section>
    <StatusSectionLoader v-if="loading" type="incident" is-open />
    <StatusSection
      v-else
      :title="$labels.activeIncidents"
      :link="incidentHistoryLink"
      :items="activeIncidents"
      :error-message="$labels.noItemsToShow"
      type="incident"
      is-open
    />
  </DsPageContent>
</template>

<script>
import { computed } from "vue";
import { camelCase } from "lodash";
import DsPageContent from "website-design-system/src/templates/page-content/page-content";
import DsHero from "@/components/hero";
import pageStatus from "@/mixins/pageStatus";
import { STATUSES, STATUSES_BY_SEVERITY, SYSTEMS } from "@/constants";
import { icons, statusToIcon } from "@/utils/mappers";
import { getLastNDaysRange } from "@/utils/date";
import {
  StatusOverview,
  StatusSection,
  StatusSectionLoader,
  StatusUptime,
} from "@/components";
import { incidentMessageMapper } from "@/mappers";
import axios from "axios";

export default {
  name: "HomePage",
  mixins: [pageStatus],
  provide() {
    return {
      loading: computed(() => this.loading),
    };
  },
  components: {
    DsPageContent,
    DsHero,
    StatusOverview,
    StatusSection,
    StatusSectionLoader,
    StatusUptime,
  },
  inject: ["screen"],
  data() {
    return {
      activeIncidents: [],
      overviewDashboard: null,
      severeMessagesPerDay: null,
      uptimeDates: getLastNDaysRange(90),
      showUptimeBar: false,
    };
  },
  computed: {
    overviewStatus() {
      // If there are active incidents, pick the overview status from high to low severity running the following logic:
      // - If there is at least 1 incident with SEVERELY_DEGRADED_PERFORMANCE status, then the overview status will be RED
      // - If not, look for at least 1 incident with DEGRADED_PERFORMANCE status, if there is, then the overview status will be YELLOW
      // - Otherview the overview status will be GREEN (OPERATIONAL) (because GREY is not affecting the operational status of the systems)
      let status, message;
      const degradedPerformanceIncident = this.degradedPerformanceIncident(
        this.activeIncidents
      );
      if (!degradedPerformanceIncident) {
        status = STATUSES.OPERATIONAL;
        message = this.$labels.allSystemsAreOperational;
      } else {
        status = degradedPerformanceIncident.severity;
        message = this.$labels[camelCase(degradedPerformanceIncident.severity)];
      }
      return {
        icon: icons[statusToIcon[status]],
        message,
      };
    },
    overviewItems() {
      let systemStatus = [];
      SYSTEMS.forEach((system) => {
        // check if there's at least one active incident with SEVERELY_DEGRADED_PERFORMANCE or DEGRADED_PERFORMANCE for that system
        const systemActiveIncidents = this.activeIncidents.length
          ? this.activeIncidents.filter(
              (incident) => incident.systemAffected === system
            )
          : [];
        const incidentToPush = this.degradedPerformanceIncident(
          systemActiveIncidents
        );
        // If there's no active incidents with SEVERELY_DEGRADED_PERFORMANCE or DEGRADED_PERFORMANCE for that system, set the status as operational
        if (!incidentToPush) {
          systemStatus.push({
            name: this.$labels[camelCase(system)],
            description: this.$labels[`${camelCase(system)}Description`],
            status: this.$labels.operational,
            icon: icons[statusToIcon[STATUSES.OPERATIONAL]],
            iconColor: icons[statusToIcon[STATUSES.OPERATIONAL]].color, // icons['checkmark-green'].color
          });
        } else {
          // Otherwise, set the corresponding status
          systemStatus.push({
            name: this.$labels[camelCase(system)],
            description: this.$labels[`${camelCase(system)}Description`],
            status: this.$labels[camelCase(incidentToPush.severity)],
            icon: icons[statusToIcon[incidentToPush.severity]],
            iconColor: icons[statusToIcon[incidentToPush.severity]].color,
          });
        }
      });
      return systemStatus;
    },
    legendItems() {
      return [
        {
          text: this.$labels.operational,
          color: "green",
          icon: icons[statusToIcon.OPERATIONAL],
        },
        {
          text: this.$labels.degradedPerformance,
          color: "orange",
          icon: icons[statusToIcon.YELLOW],
        },
        {
          text: this.$labels.severelyDegradedPerformance,
          color: "red",
          icon: icons[statusToIcon.RED],
        },
      ];
    },
    incidentHistoryLink() {
      return {
        content: this.$labels.showOlderIncidents,
        url: "/incident-history",
        iconRight: { category: "arrows", name: "chevron-right-small" },
      };
    },
  },
  async created() {
    // Set page loading state
    this.setPageLoading();

    // Enable uptime bar only when query param is present
    this.showUptimeBar = !!this.$route.query?.uptime;
    if (this.showUptimeBar) {
      // eslint-disable-next-line no-console
      console.info("ℹ️ Uptime bar enabled!");
    }

    // Fetch data
    await this.getAllMessages();
    await this.getAllSevereMessages();

    // Set page success state
    this.setPageSuccess();
  },
  methods: {
    // Get the degraded performance incident with the higher severity of the given activeIncidents list
    // If there is NO incident with SEVERELY_DEGRADED_PERFORMANCE or DEGRADED_PERFORMANCE then return `null`
    degradedPerformanceIncident(activeIncidents) {
      return (
        activeIncidents?.find(
          (el) =>
            STATUSES_BY_SEVERITY[el.severity] ===
            STATUSES.SEVERELY_DEGRADED_PERFORMANCE
        ) ||
        activeIncidents.find(
          (el) =>
            STATUSES_BY_SEVERITY[el.severity] === STATUSES.DEGRADED_PERFORMANCE
        ) ||
        null
      );
    },
    async getActiveIncidentMessages() {
      try {
        const { data } = await axios.get(`/api/incident-messages/active`);
        return data.incidentMessageCollection.items.map((el) =>
          incidentMessageMapper(el, this.$labels)
        );
      } catch (e) {
        return new Error(`Active incident messages fetching failed - ${e}`);
      }
    },
    async getAllSevereMessages() {
      try {
        const PAGE_SIZE = 100;
        let items = [];
        // const dates = getLastNDaysRange(90)
        const { data } = await axios.get(`/api/incident-messages/severe`, {
          params: {
            startDate: this.uptimeDates.startDate,
            endDate: this.uptimeDates.endDate,
            limit: PAGE_SIZE,
            skip: 0,
          },
        });
        items = data.incidentMessageCollection.items.map((el) =>
          incidentMessageMapper(el, this.$labels)
        );
        // pagination
        const numberOfRequests = Math.ceil(
          data.incidentMessageCollection.total / PAGE_SIZE
        );
        if (numberOfRequests > 1) {
          for (let index = 1; index < numberOfRequests; index++) {
            const pageParams = {
              startDate: this.uptimeDates.startDate,
              endDate: this.uptimeDates.endDate,
              limit: PAGE_SIZE,
              skip: index * PAGE_SIZE,
            };

            const pageResponse = await axios.get(
              `/api/incident-messages/severe`,
              { params: pageParams }
            );
            items = items.concat(
              pageResponse.data?.incidentMessageCollection?.items.map((el) =>
                incidentMessageMapper(el, this.$labels)
              )
            );
          }
        }
        this.severeMessagesPerDay = items;
      } catch (e) {
        return new Error(`Active incident messages fetching failed - ${e}`);
      }
    },
    async getAllMessages() {
      try {
        this.activeIncidents = await this.getActiveIncidentMessages();
      } catch (e) {
        this.setPageError();
        this.router.push("/error");
      }
    },
  },
};
</script>
